import { s, Shape } from "@kosmod/pancake";

/**
 * Many services have a "Delta" type, a type that contains information
 * on how to transform data across many different types.
 * 
 * This is a common implementation of a "Map" delta, describing
 * a change to a set of keyed values.
 */
export type MapDelta<TKey, TValue> = (
  | { type: 'added', k: TKey, v: TValue }
  | { type: 'updated', k: TKey, v: TValue }
  | { type: 'removed', k: TKey }
)[];

export const mapDeltaDef = <TKey extends Shape.Any, TValue extends Shape.Any>(keyDef: TKey, valueDef: TValue) => {
  return s.a(s.u([
    s.o({ type: s.l('added'), k: keyDef, v: valueDef }),
    s.o({ type: s.l('updated'), k: keyDef, v: valueDef }),
    s.o({ type: s.l('removed'), k: keyDef }),
  ] as const));
}

export const applyMapDelta = <TKey, TDelta, TStored>(
  map: Map<TKey, TStored>,
  delta: MapDelta<TKey, TDelta>,
  applyValue: (delta: TDelta) => TStored,
) => {
  for (const deltaEntry of delta) {
    switch (deltaEntry.type) {
      case 'added':
        if (map.has(deltaEntry.k))
          throw new Error(`added: Key already exists`);
        map.set(deltaEntry.k, applyValue(deltaEntry.v));
        break;
      case 'removed':
        if (!map.delete(deltaEntry.k)) {
          throw new Error(`removed: Key does not exist`);
        }
        break;
      case 'updated':
        if (!map.has(deltaEntry.k))
          throw new Error(`updated: Key does not exist`);
        map.set(deltaEntry.k, applyValue(deltaEntry.v));
        break;
    }
  }
}

export type SetDelta<TValue extends Shape.Type.Any> = (
  | { type: 'added', v: TValue }
  | { type: 'removed', v: TValue }
)[];
export const setDeltaDef = <TValue extends Shape.Any>(valueDef: TValue) => {
  return s.a(s.u([
    s.o({ type: s.l('added'), v: valueDef }),
    s.o({ type: s.l('removed'), v: valueDef }),
  ]))
}

export type MapAction<TKey, TAdd, TUpdate = TAdd> =
  | { type: 'add', key: TKey, add: TAdd }
  | { type: 'update', key: TKey, update: TUpdate }
  | { type: 'remove', key: TKey }

export const mapActionDef = <TKey extends Shape, TAdd extends Shape, TUpdate extends Shape>(
  key: TKey, add: TAdd, update: TUpdate,
) => s.du('type', {
  'add': { key, add },
  'update': { key, update },
  'remove': { key },
})

export const MapEvent = <K, V, U>() => ({
  add(key: K, add: V) {
    return { type: 'add', key, add } as const;
  },
  update(key: K, update: U) {
    return { type: 'update', key, update } as const;
  },
  remove(key: K,) {
    return { type: 'remove', key } as const;
  },
})

export const updateMap = <TKey, TAdd, TUpdate = TAdd>(
  map: { add(key: TKey, value: TAdd): void, update(key: TKey, update: TUpdate): void, remove(key: TKey): void },
  action: MapAction<TKey, TAdd, TUpdate>
) => {
  switch (action.type) {
    case 'add':
      map.add(action.key, action.add);
      return;
    case 'remove':
      map.remove(action.key);
      return;
    case 'update':
      map.update(action.key, action.update);
      return;
  }
}

export const updateSimpleMap = <TKey, TValue>(
  map: Map<TKey, TValue>,
  action: MapAction<TKey, TValue, TValue>
) => {
  switch (action.type) {
    case 'add':
      map.set(action.key, action.add);
      return;
    case 'remove':
      map.delete(action.key);
      return;
    case 'update':
      map.set(action.key, action.update);
      return;
  }
}

export const createArrayUpdater = <TKey, TValue, TUpdate>(
  matchKey: (key: TKey, value: TValue) => boolean,
  updateValue: (value: TValue, action: TUpdate) => TValue,
) => (array: TValue[], action: MapAction<TKey, TValue, TUpdate>) => {
  let next: TValue[];

  switch (action.type) {
    case 'add':
      // "action.key" is ignored
      next = [...array, action.add];
      break;
    case 'update':
      next = array.map(value => {
        if (matchKey(action.key, value)) {
          return updateValue(value, action.update);
        }
        return value;
      })
      break;
    case 'remove':
      next = array.filter(value => {
        return !matchKey(action.key, value);
      })
      break;
  }

  return next;
}
export const createMutableMapUpdater = <TKey, TValue, TUpdate>(
  updateValue: (value: TValue, action: TUpdate) => TValue,
) => (map: Map<TKey, TValue>, action: MapAction<TKey, TValue, TUpdate>) => {
  switch (action.type) {
    case 'add':
      return map.set(action.key, action.add);
    case 'remove':
      return map.delete(action.key);
    case 'update':
      const prev = map.get(action.key) as TValue;
      return map.set(action.key, updateValue(prev, action.update));
  }
}

export type SetAction<TValue> =
  | { type: 'add', add: TValue }
  | { type: 'remove', remove: TValue }


export const createPropertyUpdater = <TKey extends string, T, TValue extends { [key in TKey]: T }>(
  key: TKey,
  mutate: boolean = false,
) => (
  subject: TValue,
  action: TValue,
) => {
  if (mutate)
    return (subject[key] = action[key], subject);
  else
    return { ...subject };
}
